/**
 * Language as the controls: the machinery that lets a typed sentence become a
 * PROPOSED pass — never a running one.
 *
 * "Challenge the levitation claim" goes down the ordinary ask channel, and the
 * reply may end with one machine line — the trailer below — naming the passes
 * the model understood the person to be asking for. Everything about the shape
 * keeps invariant 9 intact:
 *
 *  - **A proposal is DATA.** `ProposedAct` is a kind, a node id and a few
 *    words of why — JSON through and through, never a closure — and it is
 *    validated here against the app's own vocabulary and the session's own
 *    nodes before any surface sees it. The model's text can suggest; only the
 *    app's tables decide what a chip says, and only a person's press runs one
 *    (`ui/actPress.ts`, the one dispatcher).
 *  - **A misread costs nothing.** A malformed trailer, an invented node id, an
 *    unknown verb — each degrades to fewer chips or none, never to a wrong
 *    spend. The reply's prose still stands.
 *  - **The trailer never reaches the reader.** `actsStreamFilter` holds the
 *    display back only as far as a partial sentinel could reach, so prose
 *    still streams token by token while a trailer that begins mid-stream is
 *    swallowed whole.
 *
 * Pure and engine-local: `chat.ts` wires it into the ask call, the UI renders
 * what survives validation. Nothing here touches the store or the network.
 */

import type { Session } from '../core/types'
import type { ActKind } from './command'

/** The trailer's sentinel, at the start of its own line. ASCII on purpose —
 *  a model asked to emit exotic brackets sometimes paraphrases them, and a
 *  sentinel that arrives paraphrased is prose with JSON in it. */
export const ACTS_MARK = '@@ACTS@@'

/** One proposed pass, validated: the kind is the app's own vocabulary, the
 *  node was on the graph when the reply landed, the why is clipped. */
export interface ProposedAct {
  kind: ActKind
  node: string
  why?: string
}

/** Every kind a proposal may name — `parseActs`'s whitelist. The composer's
 *  typed acts and these are one vocabulary (`engine/command.ts`), so a verb
 *  the box accepts is a verb the model may propose, and no other. */
const ACT_KINDS: ReadonlySet<string> = new Set([
  'expand',
  'deepen',
  'challenge',
  'diverge',
  'experiment',
  'invert',
  'verify',
  'panel',
  'audit',
  'record',
  'edit',
] satisfies ActKind[])

const MAX_ACTS = 3
const MAX_WHY = 60

/** The id table's heading, exported so the tests that keep it OUT of the
 *  cached blocks look for the words it actually prints. */
export const NODE_TABLE_HEAD = 'THE NODES ON THE GRAPH'

/**
 * The id table the protocols quote from — a proposal's node, and the marks an
 * answer puts after a sentence that rests on one (`engine/refs.ts`). Ids,
 * because labels collide and drift — a proposal naming a label would need
 * fuzzy matching at the exact moment money is about to be offered — and the
 * same cap as the graph brief, so a huge graph costs bounded tokens. Arrival
 * order: the newest nodes are the ones a conversation is usually about, and
 * they must not be the ones the cap drops, so the tail is kept and the HEAD is
 * elided.
 */
export function actsTable(s: Pick<Session, 'nodes' | 'order'>): string {
  const CAP = 80
  const rows = s.order
    .map((id) => s.nodes[id])
    .filter((n) => n !== undefined)
    .map((n) => `- ${n.id} — ${n.label}`)
  const kept = rows.length > CAP ? rows.slice(rows.length - CAP) : rows
  const elided = rows.length - kept.length
  return `${NODE_TABLE_HEAD} — the only ids a proposal or a mark may use${
    elided ? ` (the ${elided} oldest are omitted for length)` : ''
  }
${kept.join('\n')}`
}

/**
 * The one sentence the protocol ends on, and the reason the table is safe to
 * send at all. The ids are there because the LINE needs them — labels collide
 * and drift, see `actsTable` — but a model shown a table of ids will write them
 * into its prose too, and a reader was told what "n_muesrfwp005" would confirm.
 * The line is parsed and never shown; the prose is read by a person, who knows
 * a claim only by its label. The one other place an id may go is a mark after a
 * sentence (`refsProtocol`, which always rides after this one), because the
 * surface prints a label there and never the id (D295).
 */
const IDS_STAY_IN_THE_LINE =
  'The ids in the table below belong in that line, or in the brackets of a mark (below), and nowhere else. In your prose, name a claim by its label in quotes, never by its id.'

/**
 * The protocol, appended to the chat contract when a surface wants proposals.
 * It is strict about the one thing that matters — the line's shape and that
 * emitting it is OFFERING, not running — and explicit that a plain question
 * gets no line at all, so the ordinary ask stays exactly what it was.
 *
 * The id table it quotes from is NOT in here, and the split is a billing fact
 * rather than a style: the rules never change and the table changes every time
 * a pass lands, so the caller sends the rules inside the cached system block
 * and the table as `systemTail`, the uncached block rendered straight after it.
 * "The table below" is still true — it is the next thing the model reads.
 */
export function actsProtocol(): string {
  return `THE PASSES THE PERSON CAN RUN
Reasoning moves on a node: expand (the Branch button), deepen, challenge, diverge, experiment (the Test button), invert. Checks on a node: verify (a source check), panel (three lenses), audit (a model from another company cross-examines). Free forms on a node: record (opens the form where they write what actually happened), edit (opens the editor on the claim).

When their message asks you to RUN one of these — an imperative like "challenge that", "check the levitation claim against sources", "go deeper on the second one" — reply with at most one short sentence saying what you understood, then end with ONE line, alone, in exactly this shape:
${ACTS_MARK} [{"kind":"challenge","node":"<an id from the table below>","why":"<at most eight words>"}]

Rules for that line: up to ${MAX_ACTS} proposals; "kind" must be one of expand, deepen, challenge, diverge, experiment, invert, verify, panel, audit, record, edit; "node" must be an id copied exactly from the table — never invented, never a label. If their message is a question, answer it and emit NO line. If it is both, answer first, then the line. You cannot run anything: the line only offers buttons, and pressing one is theirs alone.

${IDS_STAY_IN_THE_LINE}`
}

/**
 * A display filter for the streaming reply: emits prose as it arrives and
 * holds back only what could still turn out to be the trailer.
 *
 * The obvious filter — hold everything until the next newline — kills the
 * streaming feel, because chat answers are whole paragraphs between newlines.
 * This one holds back at most `\n` + the sentinel's length: the boundary of
 * what is safe to show is everything except a suffix that is a prefix of the
 * sentinel line. Once the sentinel is matched, display locks at its start and
 * the rest accumulates for the parser.
 */
export function actsStreamFilter(): {
  /** Feed a delta in; get the newly displayable text out. */
  push(delta: string): string
  /** The stream ended: any held-back suffix that never became the trailer. */
  flush(): string
  /** The whole reply, split: what the reader sees, and the trailer or null. */
  result(): { shown: string; trailer: string | null }
} {
  const NEEDLE = `\n${ACTS_MARK}`
  let text = ''
  let shownLen = 0
  let markAt = -1

  const boundary = (): number => {
    if (markAt >= 0) return markAt
    // A reply that IS the trailer, with no prose before it.
    if (text === ACTS_MARK.slice(0, text.length)) return 0
    if (text.startsWith(ACTS_MARK)) {
      markAt = 0
      return 0
    }
    const at = text.indexOf(NEEDLE)
    if (at >= 0) {
      markAt = at
      return at
    }
    // Hold back the longest suffix that could still grow into the needle.
    for (let held = Math.min(NEEDLE.length - 1, text.length); held > 0; held--) {
      const tail = text.slice(text.length - held)
      if (NEEDLE.startsWith(tail)) return text.length - held
    }
    return text.length
  }

  return {
    push(delta) {
      text += delta
      const b = boundary()
      if (b <= shownLen) return ''
      const out = text.slice(shownLen, b)
      shownLen = b
      return out
    },
    flush() {
      if (markAt >= 0 || text.startsWith(ACTS_MARK)) return ''
      const out = text.slice(shownLen)
      shownLen = text.length
      return out
    },
    result() {
      const at = markAt >= 0 ? markAt : text.startsWith(ACTS_MARK) ? 0 : -1
      if (at < 0) return { shown: text, trailer: null }
      const line = text.slice(at).replace(/^\n/, '')
      return { shown: text.slice(0, at).trimEnd(), trailer: line.slice(ACTS_MARK.length).trim() }
    },
  }
}

/**
 * The trailer, validated into proposals — or into nothing, which is always the
 * safe direction. Model output is DATA here: an unknown kind, an id not on the
 * graph, a why past its clip, a payload that is not an array — each is dropped
 * field-by-field or row-by-row, never repaired, and never allowed to name a
 * chip the app's own tables would not.
 */
export function parseActs(trailer: string, s: Pick<Session, 'nodes'>): ProposedAct[] {
  let parsed: unknown
  try {
    parsed = JSON.parse(trailer)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  const out: ProposedAct[] = []
  const seen = new Set<string>()
  for (const row of parsed) {
    if (out.length >= MAX_ACTS) break
    if (!row || typeof row !== 'object') continue
    const { kind, node, why } = row as Record<string, unknown>
    if (typeof kind !== 'string' || !ACT_KINDS.has(kind)) continue
    if (typeof node !== 'string' || s.nodes[node] === undefined) continue
    // One chip per (kind, node): a model that stutters must not mint two
    // buttons for one spend.
    const key = `${kind}:${node}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({
      kind: kind as ActKind,
      node,
      ...(typeof why === 'string' && why.trim() ? { why: why.trim().slice(0, MAX_WHY) } : {}),
    })
  }
  return out
}
